// Partner Brand Ownership — the broker / brokerage portal's read of the
// completions THEIR OWN prequalification site produced.
//
// There is deliberately no broker id, brokerage id, or brand id in any of these
// calls: the server resolves the caller's site from the authenticated session's
// PortalUser row (LinkedBrokerId / LinkedBrokerageId) on every request. A client
// cannot widen or redirect the scope, because it never names it.

import { portalApi } from "./client";
import type { PrequalInviteStatus } from "@/design-system/lib/prequalInviteStatus";
// Type-only, and deliberately the FUNNEL's own result shape rather than a copy:
// assisted completion drives the same form through the same submission path, so
// a second declaration could only ever drift.
import type { PrequalFinalizeResult } from "./prequalification";

/** "Own" — the caller owns this site outright. "Inherited" — a broker with no
 *  site of their own, reading their covering firm's shared site. */
export type PrequalSiteOwnership = "Own" | "Inherited";

export type PrequalRole = "Buyer" | "Seller";
export type PrequalOutcome = "Pass" | "SoftDecline";
export type PrequalDocType = "Letter" | "DealSheet" | "Analysis";

/** List ordering, shared by all three list endpoints.
 *
 *  Declared HERE rather than beside the panel props that consume it: it is a
 *  wire value the three param types below need, and putting it in a UI module
 *  would point the api layer at the tabs. `tabs/prequalification/panelProps`
 *  re-exports it so a panel can take the whole list vocabulary from one import.
 *
 *  "newest" is the server default and reproduces the fixed DESC ordering these
 *  lists had before sorting existed — so an omitted `sort` is not a behaviour
 *  change for any existing caller. */
export type PrequalSort = "newest" | "oldest";

export interface PrequalSite {
  brandId: string;
  slug: string;
  displayName: string;
  referenceCode: string;
  isActive: boolean;
  ownership: PrequalSiteOwnership;
}

export interface PrequalDocument {
  documentId: string;
  docType: PrequalDocType;
  generatedAtUtc: string;
}

/** A fresh, short-lived read link for one generated artifact. `url` is a
 *  presigned blob URL that expires — it is fetched at the moment of the click
 *  and never held, so nothing here is stable enough to store or bookmark. */
export interface PrequalDocumentLink {
  documentId: string;
  docType: PrequalDocType;
  url: string;
  expiresAtUtc: string;
}

// ── Answer display rows ─────────────────────────────────────────────────────
// The mirror of Core/Prequalification/PrequalAnswerDisplay.cs.
//
// 🔴 These are TYPE-ONLY on purpose, and the render ORDER lives in
// `../tabs/prequalification/panelProps` rather than here. Both test files that
// exercise this surface replace this module wholesale with a `vi.mock` factory,
// so a runtime value exported from here would be `undefined` inside them —
// types are erased at compile time and are unaffected.

/** How much explanation one field needs. A property of the FIELD, decided
 *  server-side when it is registered — the client never derives it and never
 *  branches on it. Arrives as the enum's NAME: the API registers a global
 *  `JsonStringEnumConverter`, so every enum on this wire is its string name. */
export type PrequalAnswerTier = "Label" | "LabelWithSentence" | "Conditional";

/** Which section of the drawer a row belongs to. */
export type PrequalAnswerGroup =
  | "About"
  | "Reported"
  | "Calculated"
  | "Readiness"
  | "Screening"
  | "Documents";

/** One already-explained row: a field in plain English, its value ALREADY
 *  FORMATTED, and its explanation ALREADY CHOSEN.
 *
 *  The client does no formatting, no tier logic, and no conditional wording. A
 *  figure re-derived here is a figure that can disagree with the Letter the
 *  applicant is already holding — the server reads the stored snapshot, which
 *  is deliberately never recomputed. */
export interface PrequalAnswerDisplayItem {
  /** Stable identifier. Not shown — what a test pins a row by. */
  fieldKey: string;
  label: string;
  tier: PrequalAnswerTier;
  /** Ready to print. "Not recorded" for a field absent from an older engine
   *  snapshot — never an omitted row, so a reader can tell "we did not ask"
   *  from "they did not answer". */
  displayValue: string;
  /** Null for tier `Label`; present for `LabelWithSentence` and `Conditional`. */
  explanation: string | null;
  /** `string`, not {@link PrequalAnswerGroup}: the server types this member as
   *  a plain string, so a value outside the union is reachable on the wire even
   *  though the producer only ever writes a constant. The drawer surfaces an
   *  unrecognized group rather than silently dropping its rows — narrowing this
   *  to the union here would make that impossible to write. */
  group: string;
}

export interface PrequalCompletion {
  id: string;
  referenceNumber: string | null;
  role: PrequalRole;
  firstName: string;
  lastName: string;
  outcome: PrequalOutcome;
  qualifiedAmount: number | null;
  supportablePriceLow: number | null;
  supportablePriceHigh: number | null;
  completedAtUtc: string;
  validThroughUtc: string | null;
  hasLetter: boolean;
  hasDealSheet: boolean;
  /** Which arm sized `qualifiedAmount`. "InternalBuyer" means the headline is
   *  the TOP of the range the business supports; anything else (null included)
   *  means it is the MIDPOINT of a band, and calling it a ceiling is false. */
  buyerDocumentPath?: string | null;
}

/** `site` is null exactly when no prequalification site resolves for this
 *  account — the tab's "no site linked yet" empty state. `items` is empty in
 *  that case by construction. */
export interface PrequalCompletions {
  site: PrequalSite | null;
  items: PrequalCompletion[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** The drill-in: adds the applicant's contact details, the generated-artifact
 *  metadata, and the explained answer rows.
 *
 *  Still carries no internal status / assignment / conversion fields and no raw
 *  JSON column — `answers` is the curated projection of what the applicant told
 *  us, built server-side by the explainer registry. The raw `AnswersJson`,
 *  `EngineInputsJson`, `KnockoutReasonsJson`, `UnsureFlagsJson` and
 *  `ReadinessFlagsJson` columns are read to BUILD those rows and never cross
 *  this boundary in any form. */
export interface PrequalCompletionDetail {
  id: string;
  referenceNumber: string | null;
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  outcome: PrequalOutcome;
  qualifiedAmount: number | null;
  supportablePriceLow: number | null;
  supportablePriceHigh: number | null;
  completedAtUtc: string;
  validThroughUtc: string | null;
  documents: PrequalDocument[];
  /** Grouped and ordered by the server. The drawer groups by `group` and omits
   *  a section with no rows; it never re-orders within a group. */
  answers: PrequalAnswerDisplayItem[];
}

/** The EFFECTIVE invite status — the persisted lifecycle with expiry folded in
 *  server-side. Never the raw column. */
/** Re-exported from the shared vocabulary, not declared here.
 *
 *  This was a hand-maintained seven-member mirror of the staff union's eight,
 *  so an identical-looking `isTerminal` in this portal and on the staff pages
 *  compiled against two different domains. Imported as well as re-exported: a
 *  bare `export type { … } from` creates no local binding, and the DTOs below
 *  reference the name. */
export type { PrequalInviteStatus };

export interface PrequalInvite {
  id: string;
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerBrandId: string | null;
  partnerSlug: string | null;
  status: PrequalInviteStatus;
  sentAtUtc: string;
  lastSentAtUtc: string;
  sendCount: number;
  expiresAtUtc: string;
  openedAtUtc: string | null;
  completedAtUtc: string | null;
  submissionId: string | null;
  /** Who sent it, as a display label. Null in portal context for rows the
   *  server could not attribute. */
  invitedByLabel: string | null;
  /** The full link, so a sender can copy it out and send it through their own
   *  channel instead of relying on the email. */
  inviteUrl: string;
}

/** The `PagedResult<T>` envelope — note the rows are `data`, not `items`. */
export interface PrequalInvitePage {
  data: PrequalInvite[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PrequalInviteListParams {
  status?: PrequalInviteStatus;
  /** Only the invites THIS portal user sent. The caller's identity is resolved
   *  server-side, never supplied. */
  mineOnly?: boolean;
  outstanding?: boolean;
  /** Free-text over the recipient's name and email. Blank is dropped by the
   *  client's query builder, so an empty box is indistinguishable from an
   *  unsent param. */
  q?: string;
  sort?: PrequalSort;
  page?: number;
  pageSize?: number;
}

/** The completions list. `mineOnly` is NEW here — the completions read had no
 *  scope filter at all, which is why the tab's single shared scope control had
 *  nothing to apply to this list. Absent still means the whole site. */
export interface PrequalCompletionListParams {
  q?: string;
  sort?: PrequalSort;
  mineOnly?: boolean;
  page?: number;
  pageSize?: number;
}

// ── In-progress drafts (the sender view) ────────────────────────────────────

/** A prequalification somebody STARTED from the caller's site and has not
 *  finished — the follow-up list.
 *
 *  **No answer content, in any field.** The server's shared projection has
 *  nowhere to put it: not gated on a role, not gated on a flag. Name and email
 *  are here because they are identity, and a follow-up list of anonymous rows is
 *  not actionable.
 *
 *  Also no resume token — the token authorizes every anonymous read and write of
 *  the row, so surfacing it into a screen-share or a support ticket would turn it
 *  into a permanent credential leak. */
export interface PrequalDraft {
  draftId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: PrequalRole;
  /** NULL for an ORGANIC draft — one nobody sent, which has no invite and
   *  therefore no invite status. A partner is never entitled to one (their
   *  boundary is invited drafts on their own site), but the projection is shared
   *  with the staff list where organic rows DO appear, so renderers handle null
   *  rather than trusting the scope. */
  inviteStatus: PrequalInviteStatus | null;
  createdAt: string;
  lastActivityAtUtc: string;
  /** How far through, as the numerator to `stepCount`. */
  stepIndex: number;
  /** The denominator, from the row's own funnel — 6 for a buyer, 7 for a
   *  seller. Never assume one. */
  stepCount: number;
  /** A DISPLAY classification from the server's shared staleness threshold.
   *  Never recomputed here: two derivations of "stale" disagree the first time
   *  the threshold changes. */
  isAbandoned: boolean;
}

export interface PrequalDraftPage {
  data: PrequalDraft[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PrequalDraftListParams {
  /** Only the drafts THIS portal user sent — a filter inside the site
   *  boundary, exactly like the invite list's toggle. The caller's identity is
   *  resolved server-side, never supplied. */
  mineOnly?: boolean;
  /** Free-text over the applicant's name and email. Never over their answers —
   *  the projection carries none, deliberately. */
  q?: string;
  sort?: PrequalSort;
  page?: number;
  pageSize?: number;
}

/** Everything the server accepts from a partner sending an invite.
 *
 *  The brand is deliberately absent: the site is the one resolved from the
 *  session, and a partner has no site to choose. The request record does carry
 *  `partnerBrandId` / `phoenixNative` fields for the staff path, but this path
 *  ignores them by design — sending them would imply a choice the partner does
 *  not have. */
export interface SendPrequalInvite {
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  /** Acknowledgement that a COLLEAGUE at this firm already has a live invite
   *  out to this buyer. Omitted on the first attempt, which is what makes the
   *  server answer 409 with their name; the confirm step re-submits with true.
   *
   *  Brand-scoped server-side, so this can only ever name someone on the
   *  caller's own site — never another firm's outreach. */
  confirmDuplicate?: boolean;
}

/** One live invite that already exists for the buyer being invited. */
export interface PrequalInviteDuplicate {
  inviteId: string;
  sentByLabel: string;
  sentAtUtc: string;
  lastSentAtUtc: string;
  expiresAtUtc: string;
  status: string;
}

/** The 409 body behind a duplicate-invite warning. `message` is composed
 *  server-side and rendered verbatim — a second client-composed copy is how the
 *  warning and the refused-retry error drift apart. */
export interface PrequalInviteDuplicateConflict {
  email: string;
  partnerBrandId?: string | null;
  message: string;
  existing: PrequalInviteDuplicate[];
}

// ── Assisted completion ─────────────────────────────────────────────────────
// The broker drives the client's form, in the same sitting, from their own
// signed-in machine.
//
// Every call here can answer 403 WITH AN EMPTY BODY, and that one answer covers
// both "you may not" and "no such draft" — deliberately, because a 404/403 split
// over an id-addressed endpoint is an existence oracle over every application in
// the system. Treat it as a single state.

/** Base64 on the wire. Echoed back UNMODIFIED on the next write; a missing or
 *  stale stamp is a 409, never last-write-wins. */
export type PrequalRowVersion = string;

/** What an IN-OFFICE start returns. **No token in any field** — the broker is
 *  not the visitor; they drive the form themselves and address the draft by id
 *  from here on. And no email is sent: the client is in the room. */
export interface PrequalInOfficeStart {
  draftId: string;
  inviteId: string;
  partnerBrandId?: string | null;
  partnerSlug?: string | null;
  stepIndex: number;
  stepCount: number;
  rowVersion: PrequalRowVersion | null;
}

/** The draft as the assisting broker loads it.
 *
 *  **No resume token, in any field.** That token authorizes every anonymous read
 *  and write of the row, so surfacing it into a portal UI would turn a
 *  screen-share or a support ticket into a permanent credential leak. */
export interface PrequalAssistedDraft {
  draftId: string;
  role: PrequalRole;
  firstName: string;
  lastName: string;
  email: string;
  partnerBrandId?: string | null;
  partnerSlug?: string | null;
  originatingInviteId?: string | null;
  inviteStatus?: PrequalInviteStatus | null;
  stepIndex: number;
  /** The server's own denominator for this funnel — never assume 6 or 7. */
  stepCount: number;
  answersJson: string;
  rowVersion: PrequalRowVersion | null;
  createdAt: string;
  lastActivityAtUtc: string;
  isAbandoned: boolean;
}

export interface PrequalAssistedStepBody {
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  answersJson: string;
}

export interface PrequalAssistedPersistResult {
  draftId: string | null;
  rowVersion: PrequalRowVersion | null;
  stepIndex: number;
  error: string | null;
}

/** Only the two SAME-SITTING values are accepted; the server refuses anything
 *  else (including an omitted value) as a renderable validation error rather
 *  than believing it. */
export type PrequalCompletionMode = "SameSittingClientDevice" | "SameSittingBrokerCertified";

export interface PrequalAssistedFinalizeBody {
  rowVersion: PrequalRowVersion | null;
  consentAccepted: boolean;
  answersJson: string;
  phone?: string;
  /** Honeypot — hidden on the real form; always empty for humans. */
  website?: string;
  completionMode: PrequalCompletionMode;
  /** **Polarity.** The checkbox reads "email a copy to the client" and is
   *  defaulted ON, so UNCHECKING it sends `true` here. Omitting the field SENDS
   *  the documents. */
  suppressClientCopy: boolean;
}

export const prequalSiteApi = {
  /** The caller's completions, newest first by default. Called with NO
   *  arguments on the tab's mount, where it doubles as the site resolver. */
  completions: (params: PrequalCompletionListParams = {}) =>
    portalApi.get<PrequalCompletions>("/prequal-site/completions", {
      q: params.q,
      sort: params.sort,
      mineOnly: params.mineOnly,
      page: params.page,
      pageSize: params.pageSize,
    }),

  /** One completion. 404s unless it belongs to the caller's resolved site. */
  completion: (id: string) =>
    portalApi.get<PrequalCompletionDetail>(`/prequal-site/completions/${id}`),

  /** A fresh read link for one of that completion's generated documents.
   *
   *  Scoped through the COMPLETION, not the document: the id pair is checked
   *  against the caller's resolved site on every call, so a document id lifted
   *  from another site's payload is a 404 rather than a download. Called at
   *  click time because the URL it returns is short-lived — holding one on the
   *  detail payload would hand every open drawer an expiring credential. */
  completionDocumentLink: (completionId: string, documentId: string) =>
    portalApi.get<PrequalDocumentLink>(
      `/prequal-site/completions/${completionId}/documents/${documentId}/link`,
    ),

  /** Invites sent from the caller's site. An account with no resolved site gets
   *  an EMPTY page, never an error. */
  invites: (params: PrequalInviteListParams = {}) =>
    portalApi.get<PrequalInvitePage>("/prequal-site/invites", {
      status: params.status,
      mineOnly: params.mineOnly,
      outstanding: params.outstanding,
      q: params.q,
      sort: params.sort,
      page: params.page,
      pageSize: params.pageSize,
    }),

  /** The in-progress prequalifications on the caller's site. An account with no
   *  resolved site gets an EMPTY page, never an error — the server routes that
   *  case through the same entitlement rule as every other. */
  drafts: (params: PrequalDraftListParams = {}) =>
    portalApi.get<PrequalDraftPage>("/prequal-site/drafts", {
      mineOnly: params.mineOnly,
      q: params.q,
      sort: params.sort,
      page: params.page,
      pageSize: params.pageSize,
    }),

  /** Send an invite from the caller's own site. 403s with a friendly message
   *  when no site resolves — there is nothing to invite anyone to. */
  sendInvite: (body: SendPrequalInvite) =>
    portalApi.post<PrequalInvite>("/prequal-site/invites", body),

  /** Resend one of the caller's own invites. Anything outside the caller's site
   *  404s (never 403 — that would confirm it exists). */
  resendInvite: (id: string) =>
    portalApi.post<PrequalInvite>(`/prequal-site/invites/${id}/resend`),

  revokeInvite: (id: string) => portalApi.post<void>(`/prequal-site/invites/${id}/revoke`),

  // ── Assisted completion ───────────────────────────────────────────────────

  /** Start an in-office prequalification. Mints a SUPPRESSED invite so the
   *  draft is INVITED rather than organic — without it the draft would carry a
   *  null sender, be invisible on this broker's own follow-up list, and reach
   *  Phoenix staff as an abandoned organic lead. Nothing is emailed. */
  startInOfficeDraft: (body: SendPrequalInvite) =>
    portalApi.post<PrequalInOfficeStart>("/prequal-site/drafts", body),

  assistLoad: (draftId: string) =>
    portalApi.get<PrequalAssistedDraft>(`/prequal-site/drafts/${draftId}/assist`),

  assistStep: (draftId: string, body: PrequalAssistedStepBody) =>
    portalApi.put<PrequalAssistedPersistResult>(
      `/prequal-site/drafts/${draftId}/assist/step`, body),

  assistFinalize: (draftId: string, body: PrequalAssistedFinalizeBody) =>
    portalApi.post<PrequalFinalizeResult>(
      `/prequal-site/drafts/${draftId}/assist/finalize`, body),
};
